import React, { useState, useEffect, useRef } from "react";
import MainLayout from "../Themes/MainLayout";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Button,
  MenuItem,
  Select,
  IconButton,
  Container,
  Alert,
  CssBaseline,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import Tooltip from "@mui/material/Tooltip";
import Popover from "@mui/material/Popover";
import { getStartOfWeek, formatDateRange } from "../utils/dateUtils";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import { ThemeProvider } from "@mui/material/styles";
import { Add, Flag, FilterList, Search } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function Timesheets() {
  const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
  // Initialize with current week instead of allowing any week
  const [weekStart, setWeekStart] = useState(getStartOfWeek(new Date()));
  const [timesheet, setTimesheet] = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [weekStartPickerVisible, setWeekStartPickerVisible] = useState(true);
  const [fetchedEntries, setFetchedEntries] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const employee_id = empId;
  const bo_id = boId;
  // Note state management
  const [noteAnchorEl, setNoteAnchorEl] = useState(null);
  const [currentNoteRowIdx, setCurrentNoteRowIdx] = useState(null);
  const [currentNoteDayIdx, setCurrentNoteDayIdx] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [submitClicked, setSubmitClicked] = useState(false);
  const [forceSubmit, setForceSubmit] = useState(false);
  const [openPicker, setOpenPicker] = useState(false);
  const [alert, setAlert] = useState({ type: "", msg: "" });
  const [isEditing, setIsEditing] = useState(false);
  const [deletedRows, setDeletedRows] = useState([]);
  function getDateByDayOffset(offset) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + offset);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`; // local date only
  }

  const getWeekTotalHours = () => {
    return getDayTotals().reduce((sum, dayTotal) => sum + dayTotal, 0);
  };
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        // Fetch client projects first
        const projectsRes = await fetch(
          `${BASE_URL}/employee_projects/${employee_id}/${bo_id}`
        );
        const projects = await projectsRes.json();
        setClientProjects(projects);

        // Fetch timesheet for current week
        const startDate = getDateByDayOffset(0);
        const timesheetRes = await fetch(
          `${BASE_URL}/api/timesheet/week/${employee_id}/${bo_id}/${startDate}`
        );
        const timesheetData = await timesheetRes.json();

        console.log("Initial timesheet data:", timesheetData);
        setTimesheet(
          groupTimesheetEntries(timesheetData?.data || [], weekStart)
        );

        // Fetch approval history for current week
        const historyRes = await fetch(
          `${BASE_URL}/api/timesheetTracker/approvals/history/${employee_id}/${bo_id}?weekStart=${startDate}`
        );
        const historyData = await historyRes.json();
        setApprovalHistory(historyData || []);
        console.log("Initial approval history data:", historyData);
      } catch (err) {
        console.error("Error loading initial data:", err);
        setTimesheet([]); // Ensure empty state on error
        setApprovalHistory([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (employee_id && bo_id) {
      loadInitialData();
    }
  }, [employee_id, bo_id]); // Only run when employee_id or bo_id changes

  useEffect(() => {
    const fetchTimesheetForWeek = async () => {
      if (!employee_id || !bo_id) return;

      setIsLoading(true);
      try {
        const startDate = getDateByDayOffset(0);

        // Fetch timesheet for the week
        const timesheetRes = await fetch(
          `${BASE_URL}/api/timesheet/week/${employee_id}/${bo_id}/${startDate}`
        );
        const timesheetData = await timesheetRes.json();

        console.log("Timesheet data for week:", timesheetData);
        setTimesheet(
          groupTimesheetEntries(timesheetData?.data || [], weekStart)
        );

        // Fetch approval history for the week
        const historyRes = await fetch(
          `${BASE_URL}/api/timesheetTracker/approvals/history/${employee_id}/${bo_id}?weekStart=${startDate}`
        );
        const historyData = await historyRes.json();
        setApprovalHistory(historyData || []);
        console.log("Approval history for week:", historyData);
      } catch (err) {
        console.error("Error loading timesheet for week:", err);
        setTimesheet([]);
        setApprovalHistory([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimesheetForWeek();
  }, [weekStart]); // Run when week changes

  const isWeekSubmitted = timesheet.some(
    (row) =>
      row.approval_status === "Submitted" || row.approval_status === "Approved"
  );

  const fetchRecordsForWeek = async (selectedDate) => {
    if (!employee_id || !bo_id || !selectedDate) return;

    const startOfWeek = getStartOfWeek(selectedDate);
    const formattedWeekStart = startOfWeek.toISOString().split("T")[0]; // YYYY-MM-DD

    setIsLoading(true);
    try {
      // Fetch approval history for the selected week only
      const historyRes = await fetch(
        `${BASE_URL}/api/timesheetTracker/approvals/history/${employee_id}/${bo_id}?weekStart=${formattedWeekStart}`
      );
      const historyData = await historyRes.json();
      setApprovalHistory(historyData || []);

      // Fetch timesheet
      const timesheetUrl = `${BASE_URL}/api/timesheet/week/${employee_id}/${bo_id}/${formattedWeekStart}`;
      console.log("Fetching from URL:", timesheetUrl);

      const timesheetRes = await fetch(timesheetUrl);
      const timesheetData = await timesheetRes.json();

      setTimesheet(
        groupTimesheetEntries(timesheetData?.data || [], startOfWeek)
      );
    } catch (err) {
      console.error("Error fetching records for week:", err);
      setTimesheet([]);
      setApprovalHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  const groupTimesheetEntries = (entries, weekStartDate = weekStart) => {
    const map = new Map();
    const startOfWeek = new Date(
      weekStartDate.getFullYear(),
      weekStartDate.getMonth(),
      weekStartDate.getDate()
    );

    // Log all entries with their dates
    entries.forEach((entry, index) => {
      console.log(`Entry ${index + 1}:`, {
        date: entry.timesheet_date,
        project: entry.proj_name,
        task: entry.task_description,
        hours: entry.effort_hours,
        status: entry.approval_status,
      });
    });

    for (const entry of entries) {
      const entryDate = new Date(entry.timesheet_date);
      const entryOnlyDate = new Date(
        entryDate.getFullYear(),
        entryDate.getMonth(),
        entryDate.getDate()
      );

      const dayOffset = Math.round(
        (entryOnlyDate - startOfWeek) / (1000 * 60 * 60 * 24)
      );

      console.log(
        `Processing entry: ${entry.timesheet_date}, Day offset: ${dayOffset}, Status: ${entry.approval_status}`
      );

      // Only include entries that fall within the selected week (0-6 days)
      if (dayOffset < 0 || dayOffset > 6) {
        console.log(
          `EXCLUDING entry with date ${entry.timesheet_date} (offset: ${dayOffset})`
        );
        continue;
      }

      console.log(
        `INCLUDING entry with date ${entry.timesheet_date} (offset: ${dayOffset})`
      );

      const key = `${entry.proj_id}--${entry.task_description
        ?.trim()
        ?.toLowerCase()}`;

      if (!map.has(key)) {
        map.set(key, {
          client_id: entry.cl_id,
          client_name: entry.cl_name,
          proj_id: entry.proj_id,
          proj_name: entry.proj_name,
          task: entry.task_description,
          hours: Array(7).fill(0),
          notes: Array(7).fill(""),
          timesheet_ids: Array(7).fill(null),
          approval_status: entry.approval_status,
          editable: false,
        });
      }

      const row = map.get(key);
      row.hours[dayOffset] = parseFloat(entry.effort_hours);
      row.notes[dayOffset] = entry.notes || "";
      row.timesheet_ids[dayOffset] = entry.timesheet_id;
    }

    const finalEntries = Array.from(map.values());
    return finalEntries;
  };
  const buildTimesheetPayload = ({
    timesheet,
    existingEntries,
    employee_id,
    bo_id,
    getDateByDayOffset,
  }) => {
    const payload = [];
    const normalizeDate = (d) => {
      const dt = new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(dt.getDate()).padStart(2, "0")}`;
    };

    for (const row of timesheet) {
      for (let dayIdx = 0; dayIdx < 7; dayIdx++) {
        const hour = row.hours[dayIdx];
        if (hour > 0 && row.proj_id && row.task) {
          const dateStr = getDateByDayOffset(dayIdx);
          const noteForDay = row.notes?.[dayIdx] || "";
          const timesheetId = row.timesheet_ids?.[dayIdx];

          // Check if this entry corresponds to an existing record
          const existingEntry =
            timesheetId &&
            existingEntries.find((e) => e.timesheet_id === timesheetId);

          const isUpdate = !!existingEntry;

          const payloadItem = {
            // Conditionally include timesheet_id only if it's an update
            ...(isUpdate ? { timesheet_id: existingEntry.timesheet_id } : {}),

            bo_id,
            employee_id,
            proj_id: row.proj_id,
            timesheet_date: normalizeDate(dateStr),
            task_description: row.task,
            effort_hours: hour,
            notes: noteForDay,
            delete_flag: "N",
            data_source: "UI",
            created_by: "UI_USER",
            created_on_date: normalizeDate(new Date()),
            created_at: new Date().toISOString(),
            modified_by: "UI_USER",
            modified_on_date: normalizeDate(new Date()),
            modified_at: new Date().toISOString(),
            ...(row.approval_status === "Rejected"
              ? { approval_status: "Pending" }
              : {}),
          };

          payload.push(payloadItem);
        }
      }
    }

    // Optional debug log
    return payload;
  };

  const handleAddRow = () => {
    setTimesheet((prev) => [
      ...prev,
      {
        client_id: "",
        proj_id: "",
        task: "",
        hours: Array(7).fill(0),
        notes: Array(7).fill(""),
        approval_status: "Pending",
        editable: true,
      },
    ]);
  };

  const handleChange = (originalIdx, field, value) => {
    setTimesheet((prev) => {
      const updated = [...prev];
      const oldClientId = updated[originalIdx]["client_id"];
      updated[originalIdx][field] = value;
      if (field === "client_id") {
        // Only reset project if client is changed
        if (field === "client_id" && oldClientId !== value) {
          updated[originalIdx]["proj_id"] = "";
        }
      }
      return updated;
    });
  };

  const handleHourChange = (originalIdx, dayIdx, value) => {
    setTimesheet((prev) => {
      const updated = [...prev];
      updated[originalIdx].hours[dayIdx] = parseFloat(value) || 0;
      return updated;
    });
  };

  const getTotal = (hours) => hours.reduce((a, b) => a + b, 0);
  const normalizeDate = (d) => {
    const dt = new Date(d);
    const year = dt.getFullYear();

    const month = String(dt.getMonth() + 1).padStart(2, "0");

    const day = String(dt.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`; //  local date only
  };
  // Note icon click handler
  const handleNoteIconClick = (event, originalIdx, dayIdx) => {
    setNoteAnchorEl(event.currentTarget);
    setCurrentNoteRowIdx(originalIdx);
    setCurrentNoteDayIdx(dayIdx);
    const noteArray = timesheet[originalIdx].notes || [];
    setNoteText(noteArray[dayIdx] || "");
  };

  const handleNoteClose = () => {
    if (currentNoteRowIdx !== null && currentNoteDayIdx !== null) {
      const updated = [...timesheet];
      // Initialize notes array if missing
      if (!updated[currentNoteRowIdx].notes) {
        updated[currentNoteRowIdx].notes = Array(7).fill("");
      }
      updated[currentNoteRowIdx].notes[currentNoteDayIdx] = noteText;
      setTimesheet(updated);
    }
    setNoteAnchorEl(null);
    setCurrentNoteRowIdx(null);
    setCurrentNoteDayIdx(null);
  };

  const open = Boolean(noteAnchorEl);

  const handleSubmit = async (isForced = false) => {
    // Check total hours before submit
    if (!totalHoursValid && !isForced) {
      setShowConfirmDialog(true);
      return;
    }

    setShowConfirmDialog(false); // ensure dialog closes

    const startDateStr = getDateByDayOffset(0);
    const currentWeekStart = weekStart;
    try {
      const response = await fetch(
        `${BASE_URL}/api/timesheet/week/${employee_id}/${bo_id}/${startDateStr}`
      );
      const json = await response.json();
      const existingEntries = Array.isArray(json) ? json : json.data || [];

      const payload = buildTimesheetPayload({
        timesheet,
        existingEntries,
        employee_id,
        bo_id,
        getDateByDayOffset: (offset) => {
          const d = new Date(currentWeekStart);
          d.setDate(d.getDate() + offset);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`; //  Local date only
        },
      });

      const createPayload = payload.filter((p) => !p.timesheet_id);
      const updatePayload = payload.filter((p) => p.timesheet_id);
      const deletePayload = existingEntries.filter(
        (entryInDB) =>
          !payload.some((p) => p.timesheet_id === entryInDB.timesheet_id)
      );
      // Insert new entries
      for (const entry of createPayload) {
        const { timesheet_id, cl_id, cl_name, proj_name, ...data } = entry;
        const payload = {
          ...data,
          created_by: empId,
          modified_by: empId,
        };
        await fetch(`${BASE_URL}/api/timesheet/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      // Update existing entries
      for (const entry of updatePayload) {
        const { timesheet_id, cl_id, cl_name, proj_name, ...data } = entry;
        const payload = {
          ...data,
          modified_by: empId,
        };
        await fetch(`${BASE_URL}/api/timesheet/${timesheet_id}/${bo_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      for (const entry of deletePayload) {
        const { timesheet_id, cl_id, cl_name, proj_name, ...data } = entry;
        await fetch(
          `${BASE_URL}/api/timesheet/${timesheet_id}?bo_id=${bo_id}`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
          }
        );
      }
      setAlert({ type: "success", msg: "Timesheet submitted!" });
      // Clear alert after 3 seconds
      setTimeout(() => {
        setAlert({ type: "", msg: "" });
      }, 3000);

      const refreshedResponse = await fetch(
        `${BASE_URL}/api/timesheet/week/${employee_id}/${bo_id}/${startDateStr}`
      );
      const refreshedJson = await refreshedResponse.json();
      const refreshed = Array.isArray(refreshedJson)
        ? refreshedJson
        : refreshedJson.data || [];

      setFetchedEntries(refreshed);
      // Set rows as editable only if rejected or pending
      const grouped = groupTimesheetEntries(
        refreshed || [],
        new Date(currentWeekStart)
      ).map((row) => ({
        ...row,
        editable:
          row.approval_status === "Rejected" ||
          row.approval_status === "Pending",
      }));

      setTimesheet(grouped);
      // setTimesheet(groupTimesheetEntries(refreshed));
      setIsEditing(false);
    } catch (err) {
      console.error("Submission error:", err);
      setAlert({ type: "error", msg: "Submission error." });
    }
  };

  const handleConfirmOk = () => {
    setShowConfirmDialog(false);
    handleSubmit(true); // re-call submit after confirmation
  };

  const handleConfirmCancel = () => {
    setShowConfirmDialog(false);
  };
  const handleEditClick = () => {
    setTimesheet((prev) =>
      prev.map((row) => ({
        ...row,
        editable:
          row.approval_status === "Pending" ||
          row.approval_status === "Rejected",
      }))
    );
    setIsEditing(true);
  };

  const hasRejectedEdits = () => {
    return timesheet.some((row) => {
      if (row.approval_status !== "Rejected") return false;
      if (!row.editable) return false;

      // Check if any changes made
      const originalHours = fetchedEntries.filter(
        (e) =>
          e.task_description.trim().toLowerCase() ===
            row.original_task_description?.trim().toLowerCase() &&
          e.proj_id === row.original_proj_id
      );

      for (let i = 0; i < 7; i++) {
        const existing = originalHours.find(
          (e) => new Date(e.timesheet_date).getDay() === i
        );
        const changed = parseFloat(row.hours[i] || 0);
        if (existing && changed !== parseFloat(existing.effort_hours))
          return true;
      }

      return false;
    });
  };

  const getDayTotals = () => {
    const totals = Array(7).fill(0);
    timesheet.forEach((row) => {
      row.hours.forEach((h, i) => {
        totals[i] += parseFloat(h) || 0;
      });
    });
    return totals.map(roundToNearestQuarter);
  };

  const roundToNearestQuarter = (val) => {
    const num = parseFloat(val);
    if (isNaN(num)) return 0;
    return Math.round(num * 4) / 4;
  };

  const hourOptions = Array.from({ length: 97 }, (_, i) =>
    (i * 0.25).toFixed(2)
  ); // 0 to 24 in steps of 0.25

  const totalHoursValid = getWeekTotalHours() >= 40;
  const isRejectedWeek = timesheet.some(
    (row) => row.approval_status === "Rejected"
  );
  const hasEdits = hasRejectedEdits();

  const canSubmit =
    totalHoursValid &&
    (timesheet.every((row) => row.approval_status === "Pending") ||
      (isRejectedWeek && hasEdits));

  return (
    <MainLayout>
      <Typography variant="h4" gutterBottom fontSize="25px" fontWeight="600">
        Weekly Timesheet
      </Typography>

      {alert.msg && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.msg}
        </Alert>
      )}
      <Box display="flex" alignItems="center" gap={1} mb={2} mt={-1}>
        <IconButton
          onClick={() => {
            const newStart = new Date(weekStart);
            newStart.setDate(newStart.getDate() - 7);
            const newWeekStart = getStartOfWeek(newStart);
            setWeekStart(newWeekStart);
            fetchRecordsForWeek(newWeekStart);
          }}
        >
          <ArrowBackIosIcon fontSize="small" />
        </IconButton>
        {/* Conditionally show DatePicker or date range */}

        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body1">{formatDateRange(weekStart)}</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {/* prevent default DatePicker text field from rendering visually */}
            <Box sx={{ display: "none" }}>
              <DesktopDatePicker
                open={openPicker}
                onOpen={() => setOpenPicker(true)}
                onClose={() => setOpenPicker(false)}
                value={weekStart}
                onChange={(newValue) => {
                  if (newValue) {
                    const newWeekStart = getStartOfWeek(new Date(newValue));
                    setWeekStart(newWeekStart);
                    fetchRecordsForWeek(newValue);
                  }
                }}
                slotProps={{
                  textField: {
                    sx: { display: "none" }, // hides the TextField
                  },
                  popper: {
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [120, 190],
                        },
                      },
                    ],
                    container: document.body, // or use a more specific container if needed
                    sx: {
                      zIndex: 1300, // ensure it's above other elements
                      width: "fit-content",
                    },
                  },
                }}
                renderInput={() => null}
              />
            </Box>
          </LocalizationProvider>
          <IconButton onClick={() => setOpenPicker(true)} size="small">
            <CalendarTodayIcon fontSize="small" />
          </IconButton>
        </Box>
        <IconButton
          onClick={() => {
            const newStart = new Date(weekStart);
            newStart.setDate(newStart.getDate() + 7);
            const newWeekStart = getStartOfWeek(newStart);
            setWeekStart(newWeekStart);
            fetchRecordsForWeek(newWeekStart);
          }}
        >
          <ArrowForwardIosIcon fontSize="small" />
        </IconButton>
        {/* <Button
          variant="contained"
          onClick={handleAddRow}
          sx={{
            fontWeight: "bold",
            borderRadius: "8px",
            px: 2.5,
            height: 36,
            backgroundColor: "#2DB0E4",
          }}
          startIcon={<Add />}
        >
          Add
        </Button> */}
        <Button
          variant="contained"
          onClick={handleAddRow}
          disabled={isWeekSubmitted}
          sx={{
            fontWeight: "bold",
            borderRadius: "8px",
            px: 2.5,
            height: 36,
            backgroundColor: "#2DB0E4",
            opacity: isWeekSubmitted ? 0.6 : 1,
          }}
          startIcon={<Add />}
        >
          Add
        </Button>
        {/* Add button to quickly go to current week */}
        {getStartOfWeek(new Date()).getTime() !== weekStart.getTime() && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const currentWeek = getStartOfWeek(new Date());
              setWeekStart(currentWeek);
              fetchRecordsForWeek(currentWeek);
            }}
            sx={{
              fontWeight: "bold",
              borderRadius: "8px",
              px: 2,
              height: 36,
            }}
          >
            This Week
          </Button>
        )}
      </Box>
      <Box sx={{ width: "100%", overflowX: "auto", px: 0, py: 2, mt: "-10px" }}>
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <TableContainer
            component={Paper}
            sx={{ overflowX: "auto", width: "100%", boxSizing: "border-box" }}
          >
            <Table size="small" sx={{ minWidth: 1400, ml: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ textAlign: "center", verticalAlign: "middle" }}
                  >
                    Client
                  </TableCell>
                  <TableCell
                    sx={{ textAlign: "center", verticalAlign: "middle" }}
                  >
                    Project
                  </TableCell>
                  <TableCell
                    sx={{ textAlign: "center", verticalAlign: "middle" }}
                  >
                    Task
                  </TableCell>
                  {weekdays.map((day) => (
                    <TableCell
                      key={day}
                      sx={{
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      {day}
                    </TableCell>
                  ))}
                  <TableCell
                    sx={{ textAlign: "center", verticalAlign: "middle" }}
                  >
                    Total
                  </TableCell>
                  <TableCell
                    sx={{ textAlign: "center", verticalAlign: "middle" }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ textAlign: "center", verticalAlign: "middle" }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" color="textSecondary">
                        Loading timesheet data...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : timesheet.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ textAlign: "center", py: 6 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <CalendarTodayIcon
                          sx={{ fontSize: 48, color: "text.secondary" }}
                        />
                        <Typography
                          variant="h6"
                          color="textSecondary"
                          gutterBottom
                        >
                          No records for this week
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ maxWidth: 400, textAlign: "center" }}
                        >
                          You haven't logged any time for the week of{" "}
                          {formatDateRange(weekStart)}. Click "Add" to create
                          your first timesheet entry.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  timesheet
                    .map((row, originalIdx) => ({ row, originalIdx }))
                    .map(({ row, originalIdx }) => {
                      const clientOptions = [
                        ...new Map(
                          clientProjects.map((cp) => [cp.cl_id, cp.cl_name])
                        ).entries(),
                      ];

                      const projectOptions = clientProjects.filter(
                        (cp) => cp.cl_id === row.client_id
                      );

                      const isReadOnlyRow = !(
                        (row.approval_status === "Pending" ||
                          row.approval_status === "Rejected") &&
                        row.editable
                      );

                      return (
                        <TableRow key={row.timesheet_id || originalIdx}>
                          <TableCell
                            sx={{
                              minWidth: 100,
                              maxWidth: 140,
                              p: 0.5,
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            {isReadOnlyRow ? (
                              <Typography sx={{ fontSize: "0.90rem" }}>
                                {row.client_name}
                              </Typography>
                            ) : (
                              <Select
                                size="small"
                                fullWidth
                                sx={{
                                  fontSize: "0.85rem",
                                  height: "32px",
                                  "& .MuiSelect-select": { padding: "6px" },
                                }}
                                value={
                                  clientOptions.some(
                                    ([id]) => id === row.client_id
                                  )
                                    ? row.client_id
                                    : ""
                                }
                                onChange={(e) =>
                                  handleChange(
                                    originalIdx,
                                    "client_id",
                                    e.target.value
                                  )
                                }
                              >
                                {clientOptions.map(([id, name]) => (
                                  <MenuItem key={id} value={id}>
                                    {name}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",

                              verticalAlign: "middle",
                            }}
                          >
                            {isReadOnlyRow ? (
                              <Typography sx={{ fontSize: "0.90rem" }}>
                                {row.proj_name}
                              </Typography>
                            ) : (
                              <Select
                                size="small"
                                fullWidth
                                sx={{
                                  fontSize: "0.85rem",
                                  height: "32px",
                                  "& .MuiSelect-select": { padding: "6px" },
                                }}
                                value={
                                  projectOptions.some(
                                    (p) => p.proj_id === row.proj_id
                                  )
                                    ? row.proj_id
                                    : ""
                                }
                                onChange={(e) =>
                                  handleChange(
                                    originalIdx,
                                    "proj_id",
                                    e.target.value
                                  )
                                }
                                disabled={!row.client_id}
                              >
                                {projectOptions.map((p) => (
                                  <MenuItem key={p.proj_id} value={p.proj_id}>
                                    {p.proj_name}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: 120,
                              maxWidth: 200,
                              textAlign: "left",
                              verticalAlign: "middle",
                            }}
                          >
                            {isReadOnlyRow ? (
                              <Typography sx={{ fontSize: "0.90rem" }}>
                                {row.task}
                              </Typography>
                            ) : (
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Task"
                                value={row.task}
                                onChange={(e) =>
                                  handleChange(
                                    originalIdx,
                                    "task",
                                    e.target.value
                                  )
                                }
                                sx={{
                                  "& .MuiInputBase-root": {
                                    height: "32px",
                                    fontSize: "0.85rem",
                                  },
                                  "& input": {
                                    padding: "6px 8px",
                                  },
                                }}
                              />
                            )}
                          </TableCell>
                          {row.hours.map((val, dayIdx) => (
                            <TableCell
                              key={dayIdx}
                              sx={{
                                width: 60,
                                p: 0.5,
                                textAlign: "center",
                                verticalAlign: "middle",
                              }}
                            >
                              <Box
                                position="relative"
                                height="40px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  {/* Effort input */}
                                  <TextField
                                    type="number"
                                    inputProps={{
                                      step: 0.25,
                                      min: 0,
                                      style: {
                                        fontSize: 12,
                                        padding: "4px 6px",
                                      },
                                      readOnly: isReadOnlyRow,
                                    }}
                                    disabled={isReadOnlyRow}
                                    sx={{
                                      width: "50px",
                                      // Hide spinner arrows for Chrome, Safari, Edge
                                      "& input[type=number]": {
                                        MozAppearance: "textfield",
                                        WebkitAppearance: "none",
                                        appearance: "none",
                                      },
                                      // For Firefox
                                      "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
                                        {
                                          WebkitAppearance: "none",
                                          margin: 0,
                                        },
                                    }}
                                    size="small"
                                    value={val}
                                    onChange={(e) => {
                                      const value =
                                        parseFloat(e.target.value) || 0;
                                      handleHourChange(
                                        originalIdx,
                                        dayIdx,
                                        e.target.value
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const value = roundToNearestQuarter(
                                        e.target.value
                                      );
                                      handleHourChange(
                                        originalIdx,
                                        dayIdx,
                                        value
                                      ); // send rounded value
                                    }}
                                  />
                                </Box>
                                {/* Note icon button */}
                                {!isReadOnlyRow && (
                                  <Tooltip title="Add notes" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(e) =>
                                        handleNoteIconClick(
                                          e,
                                          originalIdx,
                                          dayIdx
                                        )
                                      }
                                      sx={{
                                        position: "absolute",
                                        top: 2,
                                        right: 2,
                                        padding: 0,
                                        backgroundColor:
                                          "rgba(243, 247, 247, 0.8)", // optional for visibility
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(255, 255, 255, 0.44)",
                                        },
                                      }}
                                    >
                                      <EditIcon
                                        fontSize="small"
                                        sx={{
                                          width: "15px",
                                          color: row.notes?.[dayIdx]
                                            ? "primary.main"
                                            : "inherit",
                                        }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          ))}

                          <TableCell
                            sx={{
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            {getTotal(row.hours)}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.90rem" }}
                            >
                              {row.approval_status}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            {(row.approval_status === "Rejected" ||
                              row.approval_status === "Pending") &&
                              !row.editable && (
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setTimesheet((prev) => {
                                      const updated = [...prev];
                                      const row = updated[originalIdx];

                                      // Deep snapshot (VERY IMPORTANT)
                                      if (!row._original) {
                                        row._original = JSON.parse(
                                          JSON.stringify({
                                            task: row.task,
                                            proj_id: row.proj_id,
                                            cl_id: row.cl_id,
                                            hours: row.hours,
                                            notes: row.notes,
                                            timesheet_ids: row.timesheet_ids,
                                          })
                                        );
                                      }

                                      row.editable = true;
                                      return updated;
                                    });
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}

                            {row.editable && (
                              <>
                                <Tooltip title="Cancel Edit" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setTimesheet((prev) => {
                                        const updated = [...prev];
                                        const row = updated[originalIdx];

                                        if (row._original) {
                                          row.task = row._original.task;
                                          row.proj_id = row._original.proj_id;
                                          row.cl_id = row._original.cl_id;
                                          row.hours = [...row._original.hours];
                                          row.notes = row._original.notes
                                            ? [...row._original.notes]
                                            : [];
                                          row.timesheet_ids =
                                            row._original.timesheet_ids;
                                        }

                                        row.editable = false;
                                        delete row._original; // cleanup snapshot

                                        return updated;
                                      });
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Delete Row" arrow>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setTimesheet((prev) => {
                                        const updated = [...prev];
                                        updated.splice(originalIdx, 1); // Remove the row
                                        return updated;
                                      });
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
              {!isLoading && timesheet.length > 0 && (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{ fontWeight: "bold", textAlign: "right" }}
                  >
                    Daily Total
                  </TableCell>
                  {getDayTotals().map((total, i) => (
                    <TableCell
                      key={i}
                      sx={{ fontWeight: "bold", textAlign: "center" }}
                    >
                      {total.toFixed(1)}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
                    {getWeekTotalHours().toFixed(1)}
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              )}
            </Table>
          </TableContainer>
        </ThemeProvider>
      </Box>

      <Box mt={2} textAlign="right">
        {!isLoading && timesheet.length > 0 && (
          <>
            <Button
              variant="contained"
              onClick={() => handleSubmit(false)}
              disabled={isWeekSubmitted}
            >
              Submit Timesheet
            </Button>
            {/* <Button
              variant="contained"
              onClick={() => handleSubmit()}
            //  disabled={!canSubmit}
            >
              Submit Timesheet
            </Button> */}
            {submitClicked && !totalHoursValid && (
              <Typography color="warning.main" variant="body2" mt={1}>
                Your total working hours is below 40 hours.
              </Typography>
            )}
            {submitClicked && isRejectedWeek && !hasEdits && (
              <Typography color="error" variant="body2" mt={1}>
                Please modify at least one rejected task to re-submit.
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box sx={{ width: "100%", overflowX: "auto", px: 0, py: 2, mt: "-10px" }}>
        <ThemeProvider theme={Table_theme}>
          {!isLoading && (
            <>
              <Typography variant="h6" mt={1} mb={2}>
                Approval History - {formatDateRange(weekStart)}
              </Typography>
              {approvalHistory.length > 0 ? (
                <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 1400, ml: 1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ textAlign: "left", verticalAlign: "middle" }}
                        >
                          Date Range
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "left", verticalAlign: "middle" }}
                        >
                          Employee
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "left", verticalAlign: "middle" }}
                        >
                          Project
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "left", verticalAlign: "middle" }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "left", verticalAlign: "middle" }}
                        >
                          Approver Remarks
                        </TableCell>
                        <TableCell
                          sx={{ textAlign: "left", verticalAlign: "middle" }}
                        >
                          Approved/Rejected on
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {approvalHistory.map((entry, idx) => (
                        <TableRow key={idx}>
                          <TableCell
                            sx={{
                              minWidth: 120,
                              maxWidth: 200,
                              textAlign: "left",
                              verticalAlign: "middle",
                            }}
                          >
                            {entry.week_start_date} - {entry.week_end_date}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: 120,
                              maxWidth: 200,
                              textAlign: "left",
                              verticalAlign: "middle",
                            }}
                          >
                            {entry.employee_name}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: 120,
                              maxWidth: 200,
                              textAlign: "left",
                              verticalAlign: "middle",
                            }}
                          >
                            {entry.proj_name}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: 120,
                              maxWidth: 200,
                              textAlign: "left",
                              verticalAlign: "middle",
                            }}
                          >
                            {entry.approval_status}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: 120,
                              maxWidth: 200,
                              textAlign: "left",
                              verticalAlign: "middle",
                            }}
                          >
                            {entry.approval_remarks}
                          </TableCell>
                          <TableCell
                            sx={{
                              minWidth: 120,
                              maxWidth: 200,
                              textAlign: "left",
                              verticalAlign: "middle",
                            }}
                          >
                            {entry.approval_date}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    No approval history for this week
                  </Typography>
                </Box>
              )}
            </>
          )}
        </ThemeProvider>
      </Box>
      <Dialog open={showConfirmDialog} onClose={handleConfirmCancel}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="error" />
          Warning
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Typography fontSize="md">
              Your weekly working hours are below 40. Do you want to submit
              anyway?
            </Typography>
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleConfirmCancel}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmOk}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* </Box> */}
      <Popover
        open={open}
        anchorEl={noteAnchorEl}
        onClose={handleNoteClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box p={2} display="flex" flexDirection="column" minWidth={250}>
          <Typography variant="subtitle2" mb={1}>
            Note for {weekdays[currentNoteDayIdx || 0]}
          </Typography>
          <TextField
            multiline
            rows={3}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
          />
          <Box mt={1} display="flex" justifyContent="flex-end">
            <Button onClick={handleNoteClose} size="small" variant="contained">
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </MainLayout>
  );
}
export default Timesheets;
